import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    AutoComplete,
    AutoCompleteChangeEvent,
    AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { classNames } from 'primereact/utils';
// @ts-ignore
import './ResourceAutoComplete.scss';
// @ts-ignore
import AddCircleIcon from '../../assets/images/add-circle.svg';

export type ResourceAutoCompleteRef = {
    focus: () => void;
};

export interface ResourceAutoCompleteProps<TOption> {
    value: string;
    suggestions: TOption[];
    loading?: boolean;
    invalid?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    field?: string;
    minLength?: number;
    delay?: number;
    forceSelection?: boolean;
    dropdown?: boolean;
    /**
     * Forwarded to the underlying PrimeReact AutoComplete's input. Lets
     * callers control this field's position in the page's Tab order (e.g.
     * `-1` to remove it from sequential keyboard navigation while
     * read-only) — see GH #532.
     */
    tabIndex?: number;
    completeMethod: (event: { query: string }) => void;
    getOptionLabel: (option: TOption) => string;
    getOptionValue: (option: TOption) => string;
    getAutoSelectMatchValues?: (option: TOption) => Array<string | number | null | undefined>;
    itemTemplate?: (option: TOption) => React.ReactNode;
    onSelect: (option: TOption) => void;
    onClear: (text: string) => void;
    onTextChange?: (text: string) => void;
    /**
     * Called whenever the inner input receives focus, in addition to (not
     * instead of) this component's own internal focus handling (which
     * manages the "clear on refocus after a confirmed selection" behavior
     * below). Lets a parent grid track which field/cell currently has
     * keyboard focus — see GH #532.
     */
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    emptyActionLabel?: string;
    onEmptyAction?: (query: string) => void;
}

const normalize = (value: unknown): string =>
    value === null || value === undefined ? '' : String(value).trim().toLowerCase();

/**
 * *** FIX: the "Nowy produkt" (empty-action) panel rendered faded/clipped,
 * appearing to sit BEHIND the next row instead of floating cleanly on top
 * of it. ***
 *
 * Root cause: this component renders inside a planogram cell — a `<td>` in
 * PlanogramTabular's DataTable rows, or a `.grid-cell` in PlanogramVisual's
 * grid — and `.resource-autocomplete__empty-panel` was `position: absolute`
 * relative to `.resource-autocomplete` (`position: relative`), with
 * `z-index: 9999`. That positioning only escapes ITS OWN stacking context;
 * it does not escape a table row's normal paint order. PrimeReact's
 * `.p-datatable-tbody` rows are painted as ordinary table rows in document
 * order — the next `<tr>` paints over an absolutely-positioned descendant
 * of the PREVIOUS row's `<td>` regardless of that descendant's own
 * z-index, because z-index only resolves stacking conflicts WITHIN a
 * shared stacking context, and a table row boundary does not give the
 * panel a stacking context above its sibling rows. The same risk exists in
 * PlanogramVisual: `.grid-cell` there is documented elsewhere in this
 * codebase as clipping absolutely-positioned children (see
 * PlanogramVisual's own clipboardToast comment: "position: fixed so it
 * escapes `.grid-cell`'s overflow: hidden"). Net visible effect: the panel
 * rendered at the mathematically correct `top` offset, but the row
 * beneath it painted on top of (not behind) it — exactly the faded/
 * half-visible "Nowy produkt" look reported.
 *
 * Fix: portal the panel straight to document.body (same escape hatch
 * PlanogramVisual/PlanogramTabular already use for their clipboard
 * toasts — see their own "position: fixed ... escapes" comments) and
 * position it with `position: fixed` using the actual input's
 * getBoundingClientRect(), recomputed on scroll/resize so it tracks the
 * input if the table/grid scrolls. This guarantees the panel paints above
 * EVERY row, in its own top-level stacking context, regardless of which
 * cell/row it was opened from in either view.
 */
const EMPTY_PANEL_GUTTER_PX = 3;

const ResourceAutoCompleteInner = <TOption,>(
    {
        value,
        suggestions,
        loading = false,
        invalid = false,
        disabled = false,
        placeholder = '',
        className,
        field = 'label',
        minLength = 2,
        delay = 300,
        forceSelection = false,
        dropdown = false,
        tabIndex,
        completeMethod,
        getOptionLabel,
        getOptionValue,
        getAutoSelectMatchValues,
        itemTemplate,
        onSelect,
        onClear,
        onTextChange,
        onFocus: onFocusProp,
        emptyActionLabel = 'Add product',
        onEmptyAction,
    }: ResourceAutoCompleteProps<TOption>,
    ref: React.Ref<ResourceAutoCompleteRef>,
) => {
    const autoCompleteRef = useRef<AutoComplete>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [inputValue, setInputValue] = useState(value ?? '');
    const [isFocused, setIsFocused] = useState(false);

    const lastAutoSelectedValueRef = useRef<string | null>(null);
    const selectionClearedRef = useRef(false);
    const selectingOptionRef = useRef(false);
    const hasConfirmedSelectionRef = useRef(false);
    const suppressNextFocusClearRef = useRef(false);

    /**
     * *** FIX: "Nowy produkt" reappeared right after a selection was just
     * confirmed (Enter on a highlighted suggestion, or a click on one). ***
     *
     * hasConfirmedSelectionRef already existed to track "is inputValue
     * currently a confirmed selection, not free text the user is still
     * typing" — handleFocus already relies on it to decide whether to
     * clear the field on refocus. But shouldShowEmptyAction (below)
     * previously never consulted it: that condition only looked at the
     * CURRENT inputValue length and the CURRENT suggestions list, with no
     * notion of "did a selection just happen."
     *
     * The sequence that broke: handleSelect sets inputValue to the full
     * confirmed label (e.g. "test / 100 ML / EAN: 0326532154245145") and
     * calls onSelect(option), which bubbles up to the parent
     * (PlanogramTabular/PlanogramVisual). The parent's own product-search
     * wiring (completeMethod/onSearchProducts) can re-run against that
     * now-much-longer confirmed label on a subsequent render — searching
     * for the literal string "test / 100 ML / EAN: ..." as if it were a
     * fresh query — and correctly finds NO matching product (no product
     * is literally named that). The `suggestions` prop coming back from
     * the parent then briefly becomes an empty array again, which made
     * `suggestions.length === 0` true again, and since inputValue is long
     * and the field is still focused, every other condition in
     * shouldShowEmptyAction was already true too — so the panel
     * reappeared right after a perfectly successful selection, exactly as
     * if the user had typed an unmatched query.
     *
     * Fix: shouldShowEmptyAction must explicitly require that the CURRENT
     * inputValue is NOT a value the component itself just confirmed via
     * handleSelect/the auto-select effect. Mirrored into its own STATE
     * (not just the existing ref) so it participates correctly in the
     * render that computes shouldShowEmptyAction — a ref mutation alone
     * wouldn't reliably trigger the re-render needed to hide the panel the
     * moment confirmation happens. Every place that sets
     * hasConfirmedSelectionRef now also calls setHasConfirmedSelection
     * with the same value, keeping them in lockstep.
     */
    const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);

    useImperativeHandle(ref, () => ({
        focus: () => autoCompleteRef.current?.focus?.(),
    }));

    useEffect(() => {
        if (!value && hasConfirmedSelectionRef.current) {
            return;
        }
        setInputValue(value ?? '');
        selectionClearedRef.current = false;
        if (!value) {
            hasConfirmedSelectionRef.current = false;
            setHasConfirmedSelection(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const defaultItemTemplate = useMemo(
        () => (option: TOption) => (
            <div className="resource-autocomplete__option">
                {getOptionLabel(option)}
            </div>
        ),
        [getOptionLabel],
    );

    const shouldShowEmptyAction =
        isFocused &&
        !loading &&
        !disabled &&
        !!onEmptyAction &&
        !hasConfirmedSelection &&
        inputValue.trim().length >= minLength &&
        suggestions.length === 0;

    // -----------------------------------------------------------------------
    // Portal positioning for the empty-action panel — tracks the wrapper's
    // real screen position via getBoundingClientRect(), the same approach
    // PlanogramVisual/PlanogramTabular already use for their clipboardToast
    // overlays. Recomputed on scroll (any ancestor, via the window capture
    // listener below — table/grid containers scroll internally, not the
    // window itself, so a plain window-level non-capturing scroll listener
    // would miss those) and on resize.
    // -----------------------------------------------------------------------
    const [panelRect, setPanelRect] = useState<{ top: number; left: number; width: number } | null>(null);

    const updatePanelRect = useCallback(() => {
        const el = wrapperRef.current;
        if (!el) {
            setPanelRect(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        setPanelRect({
            top: rect.bottom + EMPTY_PANEL_GUTTER_PX,
            left: rect.left,
            width: rect.width,
        });
    }, []);

    useLayoutEffect(() => {
        if (!shouldShowEmptyAction) {
            setPanelRect(null);
            return;
        }

        updatePanelRect();

        // Capture phase so this also catches scroll events on internal
        // scroll containers (the planogram grid/table), not just the
        // window itself — scroll events don't bubble, but they ARE
        // dispatched in the capture phase up through ancestors.
        window.addEventListener('scroll', updatePanelRect, true);
        window.addEventListener('resize', updatePanelRect);

        return () => {
            window.removeEventListener('scroll', updatePanelRect, true);
            window.removeEventListener('resize', updatePanelRect);
        };
    }, [shouldShowEmptyAction, updatePanelRect]);

    const emptyMessageTemplate = useMemo((): React.ReactNode => {
        const trimmedQuery = inputValue.trim();

        if (!shouldShowEmptyAction) {
            return null;
        }

        return (
            <div
                className="resource-autocomplete__empty-action"
                onMouseDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEmptyAction?.(trimmedQuery);
                    setIsFocused(false);
                }}
            >
                <img
                    src={AddCircleIcon}
                    alt=""
                    className="resource-autocomplete__empty-icon"
                />
                <span>{emptyActionLabel}</span>
            </div>
        );
    }, [
        emptyActionLabel,
        inputValue,
        onEmptyAction,
        shouldShowEmptyAction,
    ]);

    const onSelectRef = useRef(onSelect);
    const getOptionLabelRef = useRef(getOptionLabel);
    const getOptionValueRef = useRef(getOptionValue);
    const getAutoSelectMatchValuesRef = useRef(getAutoSelectMatchValues);

    useEffect(() => {
        onSelectRef.current = onSelect;
        getOptionLabelRef.current = getOptionLabel;
        getOptionValueRef.current = getOptionValue;
        getAutoSelectMatchValuesRef.current = getAutoSelectMatchValues;
    }, [onSelect, getOptionLabel, getOptionValue, getAutoSelectMatchValues]);

    const autoSelectFireCountRef = useRef(0);
    const autoSelectWindowStartRef = useRef(0);
    const autoSelectBreakerTrippedRef = useRef(false);

    useEffect(() => {
        if (autoSelectBreakerTrippedRef.current) return;
        if (loading || suggestions.length !== 1) return;

        const option = suggestions[0];
        const query = normalize(inputValue);
        if (!query) return;

        const optionValue = getOptionValueRef.current(option);
        if (lastAutoSelectedValueRef.current === optionValue) return;

        const matchValues = getAutoSelectMatchValuesRef.current
            ? getAutoSelectMatchValuesRef.current(option)
            : [getOptionLabelRef.current(option), getOptionValueRef.current(option)];

        const isExactMatch = matchValues
            .map(normalize)
            .some(matchValue => matchValue === query);

        if (!isExactMatch) return;

        const now = Date.now();
        if (now - autoSelectWindowStartRef.current > 2000) {
            autoSelectWindowStartRef.current = now;
            autoSelectFireCountRef.current = 0;
        }
        autoSelectFireCountRef.current += 1;

        if (autoSelectFireCountRef.current > 8) {
            autoSelectBreakerTrippedRef.current = true;
            // eslint-disable-next-line no-console
            console.warn(
                '[ResourceAutoComplete] Auto-select fired abnormally often in a short window; disabling auto-select for this field to avoid a runaway render loop. Manual selection from the dropdown still works.',
            );
            return;
        }

        lastAutoSelectedValueRef.current = optionValue;
        selectionClearedRef.current = false;
        hasConfirmedSelectionRef.current = true;
        setHasConfirmedSelection(true);
        suppressNextFocusClearRef.current = true;
        setInputValue(getOptionLabelRef.current(option));
        onSelectRef.current(option);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, suggestions, loading]);

    const handleBlur = () => {
        window.setTimeout(() => setIsFocused(false), 150);

        if (!hasConfirmedSelectionRef.current && value) {
            setInputValue(value);
            selectionClearedRef.current = false;
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);

        // External focus notification (e.g. a parent grid tracking which
        // cell/field currently has keyboard focus) fires regardless of the
        // internal "clear on refocus" logic below — it's purely additive
        // and must not be skipped by the suppression/early-return paths
        // that logic uses.
        onFocusProp?.(event);

        if (suppressNextFocusClearRef.current) {
            suppressNextFocusClearRef.current = false;
            return;
        }

        if (hasConfirmedSelectionRef.current) {
            hasConfirmedSelectionRef.current = false;
            setHasConfirmedSelection(false);
            selectionClearedRef.current = false;
            lastAutoSelectedValueRef.current = null;
            setInputValue('');
            onClear('');
        }
    };

    const handleChange = (event: AutoCompleteChangeEvent) => {
        const isTextInput = typeof event.value === 'string';

        const nextText = isTextInput
            ? event.value
            : event.value
                ? getOptionLabel(event.value as TOption)
                : '';

        if (!isTextInput) {
            lastAutoSelectedValueRef.current = null;
            setInputValue(nextText);
            onTextChange?.(nextText);
            selectingOptionRef.current = true;
            return;
        }

        if (selectingOptionRef.current) {
            selectingOptionRef.current = false;
            setInputValue(nextText);
            onTextChange?.(nextText);
            return;
        }

        const currentText = normalize(nextText);
        const previousText = normalize(value);

        lastAutoSelectedValueRef.current = null;

        // *** FIX (same bug, the manual-typing path): editing the text of
        // an already-confirmed selection must immediately drop
        // hasConfirmedSelection, not just inputValue. Otherwise typing
        // over a confirmed value would keep shouldShowEmptyAction
        // suppressed even though the field no longer holds a confirmed
        // selection — the opposite failure mode from the one this fix
        // targets, but the same root cause: hasConfirmedSelection must
        // always reflect whether the CURRENT inputValue is still the
        // exact confirmed value, not whatever it was several keystrokes
        // ago. ***
        if (hasConfirmedSelectionRef.current) {
            hasConfirmedSelectionRef.current = false;
            setHasConfirmedSelection(false);
        }

        setInputValue(nextText);
        onTextChange?.(nextText);

        if (previousText && previousText !== currentText && !selectionClearedRef.current) {
            selectionClearedRef.current = true;
            onClear(nextText);
            return;
        }

        if (!currentText && !selectionClearedRef.current) {
            selectionClearedRef.current = true;
            onClear('');
        }
    };

    const handleSelect = (event: AutoCompleteSelectEvent) => {
        const option = event.value as TOption;

        selectingOptionRef.current = false;
        selectionClearedRef.current = false;
        lastAutoSelectedValueRef.current = getOptionValue(option);
        hasConfirmedSelectionRef.current = true;
        setHasConfirmedSelection(true);
        suppressNextFocusClearRef.current = true;

        setInputValue(getOptionLabel(option));
        onSelect(option);
    };

    return (
        <div className="resource-autocomplete" ref={wrapperRef}>
            <AutoComplete
                ref={autoCompleteRef}
                value={inputValue}
                suggestions={suggestions}
                field={field}
                placeholder={placeholder}
                completeMethod={completeMethod}
                forceSelection={forceSelection}
                dropdown={dropdown}
                delay={delay}
                minLength={minLength}
                disabled={disabled}
                tabIndex={tabIndex}
                className={classNames('w-full', className, {
                    'p-invalid': invalid,
                })}
                itemTemplate={itemTemplate ?? defaultItemTemplate}
                emptyMessage=""
                onChange={handleChange}
                onSelect={handleSelect}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />

            {shouldShowEmptyAction && panelRect && createPortal(
                <div
                    className="resource-autocomplete__empty-panel resource-autocomplete__empty-panel--portal"
                    style={{
                        position: 'fixed',
                        top: panelRect.top,
                        left: panelRect.left,
                        width: panelRect.width,
                    }}
                >
                    {emptyMessageTemplate}
                </div>,
                document.body,
            )}
        </div>
    );
};

const ResourceAutoComplete = forwardRef(ResourceAutoCompleteInner) as <TOption>(
    props: ResourceAutoCompleteProps<TOption> & {
        ref?: React.Ref<ResourceAutoCompleteRef>;
    },
) => React.ReactElement;

export default ResourceAutoComplete;